import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Users, Mail, TrendingUp, Search, X, Globe, Trophy, CreditCard, Download, BellRing, CircleDollarSign, Banknote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUTS = {
  "payée": { label: "Inscrit (payé)", cls: "border-[#C5A059]/60 text-[#C5A059]" },
  "en_attente": { label: "Pré-inscrit", cls: "border-[#3f3f46] text-[#A1A1AA]" },
};

const SUIVI = {
  a_relancer: { label: "À relancer", cls: "border-amber-500/60 text-amber-400" },
  contacte: { label: "Contacté", cls: "border-sky-500/60 text-sky-400" },
  converti: { label: "Converti", cls: "border-emerald-500/60 text-emerald-400" },
};

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-[#27272A] bg-[#121214] p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{label}</p>
        <Icon size={16} className="text-[#C5A059]" />
      </div>
      <p className="font-display text-3xl font-bold mt-3">{value}</p>
      {sub && <p className="text-[11px] text-[#71717A] mt-1">{sub}</p>}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-[#27272A]/60 last:border-0">
      <span className="text-[10px] uppercase tracking-widest text-[#71717A] shrink-0">{label}</span>
      <span className="text-sm text-right text-white break-all">{value}</span>
    </div>
  );
}

function SuiviSelect({ contact, onSaved, testid, disabled }) {
  const update = async (suivi) => {
    try {
      await axios.patch(`${API}/admin/crm/${contact.type}/${contact.id}/suivi`, { suivi }, { withCredentials: true });
      toast.success(`Suivi : ${SUIVI[suivi].label}`);
      onSaved(contact.id, contact.type, suivi);
    } catch {
      toast.error("Erreur de mise à jour");
    }
  };
  if (disabled) {
    return (
      <span className={`inline-block border px-2.5 py-1.5 text-[10px] uppercase tracking-widest ${SUIVI[contact.suivi || "a_relancer"].cls}`} data-testid={testid}>
        {SUIVI[contact.suivi || "a_relancer"].label}
      </span>
    );
  }
  return (
    <select
      value={contact.suivi || "a_relancer"}
      onChange={(e) => update(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
      className={`bg-[#0A0A0C] border px-2.5 py-1.5 text-[10px] uppercase tracking-widest focus:outline-none ${SUIVI[contact.suivi || "a_relancer"].cls}`}
    >
      {Object.entries(SUIVI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
    </select>
  );
}

export default function AdminCRM({ readOnly = false }) {
  const [analytics, setAnalytics] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [filtreType, setFiltreType] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("");
  const [filtreSuivi, setFiltreSuivi] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/analytics`, { withCredentials: true }).then((r) => setAnalytics(r.data)).catch(() => toast.error("Erreur statistiques"));
    axios.get(`${API}/admin/crm`, { withCredentials: true }).then((r) => setContacts(r.data)).catch(() => toast.error("Erreur contacts"));
  }, []);
  useEffect(load, [load]);

  const onSuiviSaved = (id, type, suivi) => {
    setContacts((prev) => prev.map((c) => (c.id === id && c.type === type ? { ...c, suivi } : c)));
    setSelected((prev) => (prev && prev.id === id && prev.type === type ? { ...prev, suivi } : prev));
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return contacts.filter((c) => {
      if (filtreType && c.type !== filtreType) return false;
      if (filtreStatut && (c.type !== "joueur" || c.paiement_statut !== filtreStatut)) return false;
      if (filtreSuivi && (c.suivi || "a_relancer") !== filtreSuivi) return false;
      if (!q) return true;
      const nom = c.type === "joueur" ? `${c.prenom} ${c.nom}` : c.nom;
      return [nom, c.email, c.telephone, c.sujet].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [contacts, search, filtreType, filtreStatut, filtreSuivi]);

  const maxDaily = analytics ? Math.max(...analytics.daily.map((d) => d.count), 1) : 1;

  return (
    <div data-testid="admin-crm" className="space-y-12">
      {/* ===== Statistiques de visites ===== */}
      <section>
        <h2 className="font-display text-xl uppercase font-bold mb-6">Aperçu des visites</h2>
        {analytics && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Eye} label="Visites aujourd'hui" value={analytics.visits.today} testid="crm-visits-today" />
              <StatCard icon={TrendingUp} label="7 derniers jours" value={analytics.visits.week} sub={`${analytics.visits.month} sur 30 jours`} testid="crm-visits-week" />
              <StatCard icon={Globe} label="Visites totales" value={analytics.visits.total} testid="crm-visits-total" />
              <StatCard icon={Users} label="Visiteurs uniques" value={analytics.visitors.unique_total} sub={`${analytics.visitors.unique_week} cette semaine`} testid="crm-visitors-unique" />
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mt-4">
              <div className="lg:col-span-2 border border-[#27272A] bg-[#121214] p-6" data-testid="crm-daily-chart">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-6">Visites — 14 derniers jours</p>
                <div className="flex items-end gap-1.5 h-32">
                  {analytics.daily.map((d) => (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-2 group" title={`${d.day} : ${d.count} visite(s)`}>
                      <span className="text-[9px] text-[#C5A059] opacity-0 group-hover:opacity-100 transition-opacity">{d.count}</span>
                      <div className="w-full bg-[#C5A059]/80 group-hover:bg-[#C5A059] transition-colors" style={{ height: `${Math.max((d.count / maxDaily) * 100, 2)}%` }} />
                      <span className="text-[8px] text-[#71717A] -rotate-45 origin-top-left whitespace-nowrap">{d.day}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-[#27272A] bg-[#121214] p-6" data-testid="crm-top-pages">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-5">Pages les plus vues</p>
                {analytics.top_pages.length === 0 ? (
                  <p className="text-xs text-[#3f3f46]">Aucune visite enregistrée pour le moment.</p>
                ) : (
                  <ul className="space-y-3">
                    {analytics.top_pages.map((p) => (
                      <li key={p.page} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-[#A1A1AA] truncate">{p.page === "/" ? "Accueil (/)" : p.page}</span>
                        <span className="text-[#C5A059] font-semibold shrink-0">{p.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <StatCard icon={Trophy} label="Inscrits payés" value={analytics.crm.inscrits} testid="crm-count-inscrits" />
              <StatCard icon={CreditCard} label="Pré-inscrits" value={analytics.crm.preinscrits} testid="crm-count-preinscrits" />
              <StatCard icon={Mail} label="Messages reçus" value={analytics.crm.messages} testid="crm-count-messages" />
              <StatCard icon={BellRing} label="À relancer" value={analytics.crm.a_relancer} sub="Pipeline de suivi" testid="crm-count-relancer" />
            </div>

            {analytics.revenus && (
              <div data-testid="crm-revenue-section">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <StatCard icon={CircleDollarSign} label="Total encaissé" value={`${analytics.revenus.total_reel.toLocaleString("fr-FR")} $US`}
                    sub={`${analytics.revenus.inscrits_payes} inscription(s) payée(s) confirmée(s)`} testid="crm-revenue-total" />
                  <StatCard icon={CreditCard} label="Dont carte (Stripe)" value={`${analytics.revenus.stripe_total.toLocaleString("fr-FR")} $US`} testid="crm-revenue-stripe" />
                  <StatCard icon={Banknote} label="Dont bureau / manuel" value={`${analytics.revenus.manuel_total.toLocaleString("fr-FR")} $US`} testid="crm-revenue-manuel" />
                  <StatCard icon={Trophy} label="Équivalent forfaitaire" value={`${analytics.revenus.total_forfaitaire.toLocaleString("fr-FR")} $US`}
                    sub="20 $US × inscrits payés" testid="crm-revenue-forfait" />
                </div>
                <div className="border border-[#27272A] bg-[#121214] p-6 mt-4 max-w-md" data-testid="crm-revenue-detections">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-5">Revenus par détection</p>
                  {analytics.revenus.par_detection.length === 0 ? (
                    <p className="text-xs text-[#3f3f46]">Aucune inscription payée pour le moment.</p>
                  ) : (
                    <ul className="space-y-3">
                      {analytics.revenus.par_detection.map((d) => (
                        <li key={d.detection_id} data-testid={`crm-revenue-det-${d.detection_id}`} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-[#A1A1AA] truncate">{d.nom} <span className="text-[#71717A] text-xs">({d.inscrits} payé{d.inscrits > 1 ? "s" : ""})</span></span>
                          <span className="text-[#C5A059] font-semibold shrink-0">{d.total.toLocaleString("fr-FR")} $US</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <div className="border border-[#27272A] bg-[#121214] p-6 mt-4 max-w-md" data-testid="crm-top-countries">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-5">Pays des visiteurs</p>
              {analytics.top_countries.length === 0 ? (
                <p className="text-xs text-[#3f3f46]">La localisation des visiteurs apparaîtra dès les prochaines visites.</p>
              ) : (
                <ul className="space-y-3">
                  {analytics.top_countries.map((c) => (
                    <li key={c.country} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-[#A1A1AA]">{c.country}</span>
                      <span className="text-[#C5A059] font-semibold shrink-0">{c.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </section>

      {/* ===== Contacts ===== */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h2 className="font-display text-xl uppercase font-bold">Contacts & joueurs ({filtered.length})</h2>
          {!readOnly && (
            <a
              href={`${API}/admin/crm/export.csv`}
              data-testid="crm-export-button"
              className="flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors"
            >
              <Download size={14} /> Exporter en CSV (Excel)
            </a>
          )}
        </div>
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un nom, email, téléphone…"
              data-testid="crm-search"
              className="w-full bg-[#0A0A0C] border border-[#27272A] pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]"
            />
          </div>
          <select value={filtreType} onChange={(e) => setFiltreType(e.target.value)} data-testid="crm-filter-type"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]">
            <option value="">Joueurs + Messages</option>
            <option value="joueur">Joueurs uniquement</option>
            <option value="contact">Messages uniquement</option>
          </select>
          <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)} data-testid="crm-filter-statut"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]">
            <option value="">Tous paiements</option>
            <option value="payée">Inscrits (payés)</option>
            <option value="en_attente">Pré-inscrits</option>
          </select>
          <select value={filtreSuivi} onChange={(e) => setFiltreSuivi(e.target.value)} data-testid="crm-filter-suivi"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]">
            <option value="">Tous les suivis</option>
            <option value="a_relancer">À relancer</option>
            <option value="contacte">Contacté</option>
            <option value="converti">Converti</option>
          </select>
        </div>

        <div className="overflow-x-auto border border-[#27272A]">
          <table className="w-full text-sm min-w-[900px]" data-testid="crm-table">
            <thead>
              <tr className="bg-[#121214] text-left text-[10px] uppercase tracking-widest text-[#71717A]">
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Nom</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Détail</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3 font-medium">Suivi</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#71717A]">Aucun contact pour le moment.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={`${c.type}-${c.id}`} data-testid={`crm-row-${c.id.slice(0, 8)}`} className="border-t border-[#27272A] hover:bg-[#121214] transition-colors">
                  <td className="px-4 py-3">
                    <span className={`text-[9px] uppercase tracking-widest px-2 py-1 border ${c.type === "joueur" ? "border-[#C5A059]/50 text-[#C5A059]" : "border-[#3f3f46] text-[#A1A1AA]"}`}>
                      {c.type === "joueur" ? "Joueur" : "Message"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{c.type === "joueur" ? `${c.prenom} ${c.nom}` : c.nom}</td>
                  <td className="px-4 py-3 text-xs text-[#A1A1AA]">
                    <span className="block">{c.email}</span>
                    {c.telephone && <span className="block text-[#71717A]">{c.telephone}</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#A1A1AA]">
                    {c.type === "joueur" ? `${c.poste_principal} · ${c.detection_nom}` : (c.sujet || "—")}
                  </td>
                  <td className="px-4 py-3">
                    {c.type === "joueur" ? (
                      <span className={`text-[10px] px-2 py-1 border ${(STATUTS[c.paiement_statut] || STATUTS["en_attente"]).cls}`}>
                        {(STATUTS[c.paiement_statut] || STATUTS["en_attente"]).label}
                      </span>
                    ) : <span className="text-[#3f3f46] text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <SuiviSelect contact={c} onSaved={onSuiviSaved} testid={`crm-suivi-${c.id.slice(0, 8)}`} disabled={readOnly} />
                  </td>
                  <td className="px-4 py-3 text-xs text-[#71717A]">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setSelected(c)} data-testid={`crm-view-${c.id.slice(0, 8)}`}
                      className="text-[10px] uppercase tracking-widest text-[#C5A059] border border-[#C5A059]/50 px-3 py-1.5 hover:bg-[#C5A059] hover:text-black transition-colors">
                      Voir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ===== Fiche détail ===== */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" role="dialog" aria-modal="true" data-testid="crm-detail-modal">
          <div className="absolute inset-0 bg-black/85" onClick={() => setSelected(null)} />
          <div className="relative max-w-lg w-full max-h-[85vh] overflow-y-auto bg-[#121214] border border-[#C5A059]/40 p-8">
            <button onClick={() => setSelected(null)} data-testid="crm-detail-close" className="absolute top-4 right-4 text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Fermer">
              <X size={18} />
            </button>
            <p className="text-[#C5A059] text-[10px] uppercase tracking-[0.35em] mb-2">{selected.type === "joueur" ? "Fiche joueur" : "Message de contact"}</p>
            <h3 className="font-display text-2xl uppercase font-bold pr-8">
              {selected.type === "joueur" ? `${selected.prenom} ${selected.nom}` : selected.nom}
            </h3>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest text-[#71717A]">Suivi :</span>
              <SuiviSelect contact={selected} onSaved={onSuiviSaved} testid="crm-detail-suivi" disabled={readOnly} />
            </div>
            <div className="mt-6">
              <DetailRow label="Email" value={selected.email} />
              <DetailRow label="Téléphone" value={selected.telephone} />
              <DetailRow label="Date" value={new Date(selected.created_at).toLocaleString("fr-FR")} />
              {selected.type === "joueur" ? (
                <>
                  <DetailRow label="Référence" value={selected.reference} />
                  <DetailRow label="Détection" value={selected.detection_nom} />
                  <DetailRow label="Statut" value={(STATUTS[selected.paiement_statut] || {}).label || selected.paiement_statut} />
                  <DetailRow label="Méthode paiement" value={selected.methode_paiement} />
                  <DetailRow label="Poste principal" value={selected.poste_principal} />
                  <DetailRow label="Poste secondaire" value={selected.poste_secondaire} />
                  <DetailRow label="Naissance" value={selected.date_naissance} />
                  <DetailRow label="Taille / Poids" value={[selected.taille && `${selected.taille} cm`, selected.poids && `${selected.poids} kg`].filter(Boolean).join(" / ") || null} />
                  <DetailRow label="Maillot" value={selected.taille_maillot} />
                  <DetailRow label="Club actuel" value={selected.club_actuel} />
                  <DetailRow label="Équipe D2R" value={selected.equipe} />
                  <DetailRow label="RDV bureau" value={selected.rdv_date ? `${selected.rdv_date} à ${selected.rdv_heure}` : null} />
                  <DetailRow label="Vidéo" value={selected.video_url} />
                  {selected.methode_paiement === "bureau_manuel" && <DetailRow label="Enregistré au bureau par" value={selected.cree_par} />}
                  <DetailRow label="Notes coach" value={selected.note_coach} />
                </>
              ) : (
                <>
                  <DetailRow label="Sujet" value={selected.sujet} />
                  <div className="mt-4">
                    <p className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Message</p>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed whitespace-pre-line bg-[#0A0A0C] border border-[#27272A] p-4">{selected.message}</p>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <a href={`mailto:${selected.email}`} data-testid="crm-detail-email"
                className="flex-1 text-center bg-[#C5A059] text-black px-4 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors">
                Envoyer un email
              </a>
              {selected.telephone && (
                <a href={`https://wa.me/${selected.telephone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="crm-detail-whatsapp"
                  className="flex-1 text-center border border-[#27272A] px-4 py-3 text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
                  WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
